// The Cierres redesign's own shell — ported from mantis-business-front-brinks's app-shell.tsx +
// app-sidebar.tsx. A simplified rebuild rather than a port of shadcn's full `sidebar.tsx`
// primitive (744 lines of cookie-persisted state, a mobile Sheet, keyboard shortcuts — none of
// it is in @mantis-sas/ui, and none of it changes what the shell looks like): plain state for
// collapsed/expanded, the same widths (13.5rem / 3rem) and the same two nav groups.
//
// A dedicated top-level route, not nested inside `BusinessBackoffice.jsx` — that shell is the
// rest of the app's (Dashboard, Archive, Users…) and swapping it out from inside would change
// what every other screen looks like. See `App.js` for the routes that use this instead.
//
// `brinks-scope` lives on `<aside>` and `<header>`, deliberately NOT on the root wrapper around
// `<main>`. Every real Cierres page already puts `brinks-scope` on its own root div, so it loses
// nothing here — but `<main>` also renders `BusinessUsers.jsx` (the "Usuarios" route), which is
// not part of this redesign and carries no `brinks-scope` of its own. `brinksTheme.css`'s
// Bootstrap-collision fix (`.brinks-scope .bg-primary` etc, `!important`) was leaking through the
// old root-wrapper scope into that page's own `.bg-primary`/`.text-primary` and re-tinting them —
// invisible-looking permission pills were the symptom that gave it away.
import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  LayoutGrid,
  Lock,
  LogOut,
  MessageSquare,
  PanelLeft,
  Settings,
  Users,
} from "lucide-react";
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@mantis-sas/ui";

import { selectAuthState } from "../../../store/selectors/authSelectors";
import { businessLogout } from "../../../ApiCalls/authentication";
import { getMyAccess } from "../../../ApiCalls/mantisAccess";
import { setCierresAccess, userLogout } from "../../../Redux/modules/auth";
import { etiquetaDeCliente, inicialesDeCliente, opcionDeCliente } from "../../../utils/cierreCliente";
import { seccionPermitida, sinSeccion } from "../../../utils/cierrePermisos";
import { ClienteCierresProvider, useClienteCierres } from "./ClienteCierresContext";
import { AvisosDeProceso } from "./AvisosDeProceso";
import { GuiaPantalla, fijarUsuarioDeGuia } from "./GuiaPantalla";
import { LogoCliente } from "./LogoCliente";
import brinksLogo from "../../../Assets/img/brinksLogo.png";
// The bars and the "B" cut from the same file, for the collapsed 3rem sidebar.
import brinksIsotipo from "../../../Assets/img/brinksIsotipo.png";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";

// Every item carries a `section` — the same `mantisBusiness.enabledSections` mechanism every
// other part of the app is toggled by from Admin ("Detalles de la empresa" → Secciones), so
// removing one there removes it from here exactly the way removing "Archivo" removes it from
// the old sidebar. Independent of `CONTROL_PANEL` (`BusinessBackoffice.jsx`'s own sidebar link
// into this shell) and of `clientType === "CONCILIACION"` (`App.js`'s route gate, which decides
// whether a business reaches this shell at all) — this is one layer finer, which of the pages
// already reached show up in the nav.
//
// BR-38 — and one layer finer again: an item also needs the person's permission for it
// (`seccionPermitida`, `utils/cierrePermisos.js`), which only bites once the business's access
// mode is `enforced`. A section is a business's choice; a permission is a person's.
const OPERACION = [
  { title: "Panel", url: "/business/backoffice/panel-cierres", icon: LayoutGrid, section: "CIERRES_PANEL" },
  { title: "Calendario", url: "/business/backoffice/calendario", icon: CalendarDays, section: "CIERRES_CALENDARIO" },
  { title: "Reportes", url: "/business/backoffice/reportes", icon: BarChart3, section: "CIERRES_REPORTES" },
  { title: "Oficinas", url: "/business/backoffice/oficinas", icon: Building2, section: "CIERRES_OFICINAS" },
];

// "Usuarios" routes to the real, already-built BusinessUsers screen (not part of this redesign)
// framed by this same shell — see App.js's route comment for why it still renders in the app's
// own Poppins styling once you land there; a full visual port is a separate task. "Asistente"
// has its own redesigned page (CierresChat.jsx) wired to the same real assistant backend as
// BusinessAssistant.jsx. "Configuración" stays disabled on purpose: the old shell's own item by
// that name (`ConfigurationSection.jsx`) is the general P2O/procurement settings module (SKUs,
// budgets, approval rules, suppliers) — real, but for a different product line, not this cierres
// tenant. Linking a Brinks user into it would be confusing, not honest. Still toggleable like
// every other item here, since a disabled placeholder is still a row on the nav to hide.
const GESTION = [
  { title: "Asistente", url: "/business/backoffice/chat", icon: MessageSquare, section: "CIERRES_ASISTENTE" },
  { title: "Alertas", url: "/business/backoffice/alertas", icon: Bell, section: "CIERRES_ALERTAS" },
  { title: "Usuarios", url: "/business/backoffice/usuarios", icon: Users, section: "CIERRES_USUARIOS" },
  { title: "Configuración", icon: Settings, section: "CIERRES_CONFIGURACION" },
];

function NavGroup({ label, items, collapsed, pathname, onNavigate }) {
  return (
    <div className="px-2 py-1.5">
      {!collapsed && (
        <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-[0.06em] text-brinks-muted-foreground">
          {label}
        </p>
      )}
      <div className="space-y-0.5">
        {items.map((item) => {
          const active = item.url && pathname === item.url;
          const disabled = !item.url;
          return (
            <button
              key={item.title}
              type="button"
              disabled={disabled}
              title={item.title}
              onClick={() => item.url && onNavigate(item.url)}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-[8px] px-2 py-1.5 text-left text-[13px] font-medium transition-colors",
                collapsed && "justify-center px-0",
                disabled
                  ? "cursor-not-allowed text-brinks-muted-foreground/50"
                  : active
                  ? "bg-primary text-primary-foreground"
                  : "text-brinks-muted-foreground hover:bg-brinks-surface-hover hover:text-brinks-foreground"
              )}
            >
              <item.icon className="h-[17px] w-[17px] shrink-0" />
              {!collapsed && <span className="truncate">{item.title}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Ported from brinks' cliente-selector.tsx, and real since B-55: the options are this business's
// own registered cierre clients (`listCierreClients`), and the one chosen here is the `clientCode`
// every Cierres screen sends. A Mantis business owning several cierre clients is the intended
// tenancy — so this is not a badge, it is how the Epago operator moves between reconciling
// Davivienda and reconciling Bancamía, and the panel endpoints refuse to guess once there are two.
//
// Each option of the menu: the client's isotipo in a 20px box beside its name, or its initials when
// it has none or the URL fails to load. The box is white on both themes, like the logo's chip.
function IsotipoCliente({ src, etiqueta }) {
  const [fallo, setFallo] = useState(false);
  const caja = "grid h-5 w-5 shrink-0 place-items-center overflow-hidden rounded-[4px] text-[9px]";

  if (!src || fallo) {
    return (
      <span aria-hidden="true" className={cn(caja, "bg-brinks-surface font-semibold text-brinks-muted-foreground")}>
        {inicialesDeCliente(etiqueta)}
      </span>
    );
  }
  return (
    <span className={cn(caja, "bg-white")}>
      <img src={src} alt="" onError={() => setFallo(true)} className="h-full w-full object-contain p-px" />
    </span>
  );
}

// It names no client: a client appears here by being registered.
function SelectorCliente() {
  const { clientes, clientCode, elegirCliente } = useClienteCierres();
  const opciones = clientes.map(opcionDeCliente).filter((o) => o.valor);
  const etiqueta = etiquetaDeCliente(clientes, clientCode);
  const elegido = opciones.find((o) => o.valor === clientCode);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Seleccionar cliente"
          title={etiqueta}
          className="inline-flex h-8 max-w-[190px] shrink-0 items-center gap-1.5 rounded-[8px] border border-brinks-border bg-card px-2.5 text-[12.5px] font-medium text-brinks-foreground transition hover:bg-brinks-surface-hover"
        >
          <LogoCliente key={elegido?.logo} src={elegido?.logo} nombre={etiqueta} className="h-4 max-w-[120px]">
            <Building2 className="h-[15px] w-[15px] shrink-0 text-brinks-muted-foreground" />
            <span className="truncate">{etiqueta}</span>
          </LogoCliente>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-brinks-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="brinks-scope font-redhat w-56 rounded-[12px] p-1">
        <DropdownMenuLabel className="px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-brinks-muted-foreground">
          Cliente
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {opciones.length === 0 ? (
          <DropdownMenuItem
            disabled
            className="flex cursor-default items-center gap-2 rounded-[8px] px-2 py-1.5 text-[13px] font-medium"
          >
            <span className="flex-1 truncate">Sin clientes</span>
          </DropdownMenuItem>
        ) : (
          opciones.map((opcion) => (
            <DropdownMenuItem
              key={opcion.valor}
              onSelect={() => elegirCliente(opcion.valor)}
              className={cn(
                "flex cursor-default items-center gap-2 rounded-[8px] px-2 py-1.5 text-[13px] font-medium",
                opcion.valor === clientCode && "bg-brinks-surface"
              )}
            >
              <IsotipoCliente key={opcion.isotipo} src={opcion.isotipo} etiqueta={opcion.etiqueta} />
              <span className="flex-1 truncate">{opcion.etiqueta}</span>
              {opcion.valor === clientCode && <Check className="h-3.5 w-3.5" />}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// The initials in the header open the account: who is signed in, for which business, and a way
// out. The sidebar keeps its own "Cerrar sesión" too — both call the shell's `handleLogout`.
function MenuPerfil({ user, empresa, initials, onLogout }) {
  const nombre =
    user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Usuario";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Cuenta"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="brinks-scope font-redhat w-60 rounded-[12px] p-1">
        <DropdownMenuLabel className="flex items-center gap-2.5 px-2 py-2 font-normal">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-[11.5px] font-semibold text-primary-foreground">
            {initials}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-semibold text-brinks-foreground">{nombre}</span>
            {user?.email && (
              <span className="block truncate text-[11.5px] text-brinks-muted-foreground">{user.email}</span>
            )}
            {empresa && (
              <span className="block truncate text-[11.5px] text-brinks-muted-foreground">{empresa}</span>
            )}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={onLogout}
          className="flex cursor-default items-center gap-2 rounded-[8px] px-2 py-1.5 text-[13px] font-medium"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          <span>Cerrar sesión</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// BR-38 — in place of a section the person may not see. Reached by typing its address, or by the
// redirect that lands every cierres business on the panel.
function SinPermiso({ texto }) {
  return (
    <div className="brinks-scope font-redhat grid min-h-[60vh] place-items-center p-6">
      <div className="max-w-md space-y-2 text-center">
        <span className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-brinks-surface text-brinks-muted-foreground">
          <Lock className="h-5 w-5" />
        </span>
        <p className="text-[14px] font-medium text-brinks-foreground">{texto}</p>
        <p className="text-[12.5px] text-brinks-muted-foreground">
          Si lo necesitas, pide a quien administra los usuarios de tu empresa que revise tu acceso.
        </p>
      </div>
    </div>
  );
}

const SECCION_DE_RUTA = Object.fromEntries(
  [...OPERACION, ...GESTION].filter((item) => item.url).map((item) => [item.url, item.section])
);

function CierresAppShell(props) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setCierresAccess: guardarAcceso } = props;
  const accesoAnterior = useRef(props.cierresAccess);
  accesoAnterior.current = props.cierresAccess;

  // BR-38 — what this person may do, read once per mount and kept in the auth state, so every
  // screen and dialog under the shell reads the same answer (`useAccesoCierres`). A refetch keeps
  // the previous answer on screen until the new one lands, so moving between pages does not
  // flicker; an assignment changed since the last page is picked up on the next. A refetch that
  // fails keeps the previous answer too: `null` ("offer what the screens always did") is only for
  // a session that never got one.
  useEffect(() => {
    let activo = true;
    const fallo = () => {
      if (activo && accesoAnterior.current === undefined) guardarAcceso({ cierresAccess: null });
    };
    getMyAccess(props.token)
      .then((res) => {
        if (!res?.success) return fallo();
        if (activo) guardarAcceso({ cierresAccess: res.data });
        return undefined;
      })
      .catch(fallo);
    return () => {
      activo = false;
    };
  }, [props.token, props.mantisBusiness?._id, guardarAcceso]);

  // Until `/access/me` answers, the nav waits rather than offering items it may take back.
  const acceso = props.cierresAccess;
  const conocido = acceso !== undefined;
  const enabledSections = props.mantisBusiness?.enabledSections || [];
  const visible = (item) => conocido && enabledSections.includes(item.section) && seccionPermitida(acceso, item.section);
  const operacion = OPERACION.filter(visible);
  const gestion = GESTION.filter(visible);
  const alertasHabilitadas = visible({ section: "CIERRES_ALERTAS" });
  const seccionActual = SECCION_DE_RUTA[location.pathname];
  const seccionNegada = conocido && seccionActual && !seccionPermitida(acceso, seccionActual) ? seccionActual : null;

  // B-174 — below `lg` the sidebar is a drawer: open from the header, closed by choosing a section,
  // touching outside it, Escape, or the screen growing to `lg`.
  const [cajon, setCajon] = useState(false);
  const ir = (url) => {
    setCajon(false);
    navigate(url);
  };
  useEffect(() => {
    if (!cajon) return undefined;
    const alEscape = (e) => { if (e.key === "Escape") setCajon(false); };
    const ancho = window.matchMedia ? window.matchMedia("(min-width: 1024px)") : null;
    const alCrecer = () => { if (ancho && ancho.matches) setCajon(false); };
    window.addEventListener("keydown", alEscape);
    if (ancho && ancho.addEventListener) ancho.addEventListener("change", alCrecer);
    return () => {
      window.removeEventListener("keydown", alEscape);
      if (ancho && ancho.removeEventListener) ancho.removeEventListener("change", alCrecer);
    };
  }, [cajon]);
  // The header's button: on a PC it folds the sidebar, below `lg` it opens the drawer.
  const alternar = () => {
    if (window.matchMedia && !window.matchMedia("(min-width: 1024px)").matches) setCajon(true);
    else setCollapsed((v) => !v);
  };

  // B-176 — the tours remember who went through them.
  fijarUsuarioDeGuia(props.userId || props.user?._id);

  const handleLogout = async () => {
    await businessLogout();
    props.userLogout();
    navigate("/");
  };

  const initials = (props.user?.name || props.user?.firstName || "U")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // The shell is the screen and `<main>` is its only scroll: the page beneath must not scroll too.
  // Anything outside the shell (a legacy toast or widget mounted after it) used to lengthen the
  // document and draw a second scrollbar beside main's.
  useEffect(() => {
    const raiz = document.documentElement;
    const antes = [raiz.style.overflow, document.body.style.overflow];
    raiz.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      [raiz.style.overflow, document.body.style.overflow] = antes;
    };
  }, []);

  // The sidebar's content, folded (`colapsada`) or not: the same in the fixed sidebar and the drawer.
  const barra = (colapsada) => (
    <>
          <div className={cn("flex h-12 items-center gap-2 border-b px-3", colapsada && "justify-center px-0")}>
            {!colapsada ? (
              // The client's logo, blue on the light theme. On the dark one the same file is drawn white
              // (`brightness-0 invert`): Brinks blue on the near-black sidebar is too dark to read.
              <img
                src={brinksLogo}
                alt="Brinks"
                className="h-[18px] w-auto max-w-full object-contain dark:brightness-0 dark:invert"
              />
            ) : (
              <img
                src={brinksIsotipo}
                alt="Brinks"
                className="h-[18px] w-auto object-contain dark:brightness-0 dark:invert"
              />
            )}
          </div>

          <div className="flex-1 overflow-y-auto py-1.5">
            {operacion.length > 0 && (
              <NavGroup
                label="Operación"
                items={operacion}
                colapsada={colapsada}
                pathname={location.pathname}
                onNavigate={ir}
              />
            )}
            {gestion.length > 0 && (
              <NavGroup
                label="Gestión"
                items={gestion}
                colapsada={colapsada}
                pathname={location.pathname}
                onNavigate={ir}
              />
            )}
          </div>

          <div className="border-t px-2 py-2">
            <button
              type="button"
              onClick={handleLogout}
              title="Cerrar sesión"
              className={cn(
                "flex w-full items-center gap-2.5 rounded-[8px] px-2 py-1.5 text-left text-[13px] font-medium text-brinks-muted-foreground transition-colors hover:bg-brinks-surface-hover hover:text-brinks-foreground",
                colapsada && "justify-center px-0"
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              {!colapsada && <span>Cerrar sesión</span>}
            </button>
            {!colapsada && (
              <div className="flex items-center gap-1.5 px-2 pb-0.5 pt-2">
                <span className="text-[10px] uppercase tracking-[0.06em] text-brinks-muted-foreground">
                  Powered by
                </span>
                <img src="/mantisCircle.png" alt="Mantis" className="h-3 w-auto object-contain opacity-80" />
              </div>
            )}
          </div>
    </>
  );

  // The provider wraps the header too, not just `<main>`: the selector is in the header, and it
  // reads the same value the screens below it do.
  return (
    <ClienteCierresProvider token={props.token} mantisBusinessId={props.mantisBusiness?._id}>
        <div className="fixed inset-0 flex w-full overflow-hidden bg-background">
        {/* B-174 — from `lg` up, the sidebar beside the page as always; below it, nothing beside the page:
            the header's button opens it as a drawer over the page. */}
        <aside
          className={cn(
            "brinks-scope font-redhat hidden h-full shrink-0 flex-col border-r bg-background transition-[width] duration-200 lg:flex",
            collapsed ? "w-[3rem]" : "w-[13.5rem]"
          )}
        >
          {barra(collapsed)}
        </aside>
        {cajon ? (
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menú">
            <button type="button" aria-label="Cerrar el menú" className="absolute inset-0 h-full w-full bg-black/40" onClick={() => setCajon(false)} />
            <aside className="brinks-scope font-redhat relative flex h-full w-[16rem] max-w-[85vw] flex-col border-r bg-background shadow-xl">
              {barra(false)}
            </aside>
          </div>
        ) : null}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="brinks-scope font-redhat sticky top-0 z-20 flex h-12 shrink-0 items-center gap-2 border-b bg-background/90 px-2.5 backdrop-blur sm:gap-2.5 sm:px-3 lg:px-6">
            <button
              type="button"
              onClick={alternar}
              aria-label="Alternar barra lateral"
              aria-expanded={cajon}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-[8px] text-brinks-muted-foreground transition hover:bg-brinks-surface-hover lg:h-8 lg:w-8"
            >
              <PanelLeft className="h-4 w-4" />
            </button>
            <div className="ml-auto flex min-w-0 items-center gap-1.5 sm:gap-2">
              <div {...guia("Cliente", "El cliente que estás viendo. Cambiarlo aquí cambia los datos de todas las pantallas.")} className="flex min-w-0">
                <SelectorCliente />
              </div>
              {/* B-176 — the guided tour of the screen on view. */}
              <GuiaPantalla pathname={location.pathname} />
              {/* B-77 — the person's own "Procesar el día" notifications; Alertas is linked inside it. */}
              <div {...guia("Avisos", "Te avisa cuando termina o falla un \"Procesar el día\" que iniciaste. Tócalo para ir a ese día.")} className="flex">
                <AvisosDeProceso token={props.token} alertasHabilitadas={alertasHabilitadas} />
              </div>
              <MenuPerfil
                user={props.user}
                empresa={props.mantisBusiness?.name}
                initials={initials}
                onLogout={handleLogout}
              />
            </div>
          </header>

          <main className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
            {seccionNegada ? <SinPermiso texto={sinSeccion(acceso, seccionNegada)} /> : props.children}
          </main>
        </div>
      </div>
    </ClienteCierresProvider>
  );
}

const seleccion = (state) => ({ ...selectAuthState(state), cierresAccess: state.auth.cierresAccess });

export default connect(seleccion, { userLogout, setCierresAccess })(CierresAppShell);
